import { JSX, ReactNode } from 'react'
import { Locale } from 'date-fns'
import {
  DEFAULT_MONTH_PROPS,
  MonthProps,
} from '../views/month/Month'
import {
  EventActions,
  ProcessedEvent,
  SchedulerContextProps,
  SchedulerView,
  SelectedRange,
} from '../types'
import {
  IntervalISOString,
  ISODateString,
} from '@pogokid/util/datetime'
import {
  DEFAULT_WEEK_OPTIONS,
  WeekProps,
} from '../views/week/types'
import {
  DayProps,
  DEFAULT_DAY_OPTIONS,
} from '../views/day/types'

export type SchedulerProviderProps =
  SchedulerProviderRequiredProps &
    Partial<SchedulerProviderOptionalProps> & {
      children?:
        | ReactNode
        | ((state: SchedulerContextProps) => ReactNode)
    }

export type ScheduleProviderPropsWithDefaults =
  SchedulerProviderRequiredProps & SchedulerProviderOptionalProps

export type EventItemClickEvent = CustomEvent<{
  processedEvent: ProcessedEvent
}>

export type EventsSelectedEvent = CustomEvent<{
  events: ProcessedEvent[]
}>

export type SelectedDateChangedEvent = CustomEvent<{
  day: ISODateString
  visibleRange: IntervalISOString
}>

export type ShowEventDetailEvent = CustomEvent<{
  eventId: string | null
}>

export type ShowCreateEventDetailEvent = CustomEvent<{
  range: SelectedRange | null
}>

export interface SchedulerProviderOptionalProps {
  /**
   * Month view settings
   * @deprecated not used
   */
  month?: MonthProps | null
  /**
   * Week view settings
   */
  week?: WeekProps | null

  /**
   * Day view settings
   * @deprecated not used
   */
  day?: DayProps | null

  /**Override viewer title component */
  viewerTitleComponent?(event: ProcessedEvent): JSX.Element

  onEventItemClick?(event: EventItemClickEvent): void

  onEventsSelected?(event: EventsSelectedEvent): void

  onShowEventDetail?(event: ShowEventDetailEvent): void

  onShowCreateEventDetail?(
    event: ShowCreateEventDetailEvent
  ): void

  /**
   * The visible date and range has changed
   * you should use this to update the events
   * given to the scheduler
   */
  onSelectedDateChanged?(
    event: SelectedDateChangedEvent
  ): void | Promise<ProcessedEvent[] | void>

  /**Async function triggered when add/edit event */
  onConfirm?(
    event: ProcessedEvent,
    action: EventActions
  ): Promise<ProcessedEvent>

  /**Async function triggered when delete event */
  onDelete?(deletedId: string | number): Promise<string | void>

  /**
   * Triggered when event is dropped on time slot.
   */
  onEventDrop?(eventId: string, droppedStartTime: Date): void
}

export interface SchedulerProviderRequiredProps {
  selectedDate: ISODateString

  /**
   * Visible calendar ids, we will only show
   * events with a calendarId in this list
   */
  calendars: Set<string | null>

  /**
   * A map of calendarId => hex colour to use
   * for events within a calendar
   */
  calendarColours: Map<string, string>

  /** Events to display */
  events: ProcessedEvent[]

  /** Events that should show as selected */
  selectedEventUids: string[]

  /**
   * The view to load
   */
  view: SchedulerView

  /**
   * date-fns Locale object
   */
  locale: Locale
}

export const SCHEDULE_PROVIDER_DEFAULT_PROPS = {
  month: DEFAULT_MONTH_PROPS,
  week: DEFAULT_WEEK_OPTIONS,
  day: DEFAULT_DAY_OPTIONS,
  view: 'week',
  direction: 'ltr',
  dialogMaxWidth: 'md',
}
